import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

type SelectedBadgeProps = {
  /** The option data for this badge */
  option: MultiSelectOptionT;
  /** Whether the entire component is disabled */
  disabled: boolean;
  /** Callback when the remove button is clicked */
  onRemove: (value: string) => void;
};

/**
 * Individual selected option badge with remove functionality
 *
 * Displays a selected option as a compact badge with an X button
 * for removal. Handles keyboard accessibility and truncates long labels.
 */
export function SelectedBadge({
  option,
  disabled,
  onRemove,
}: SelectedBadgeProps) {
  const handleRemove = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if ("key" in e && e.key !== "Enter" && e.key !== " ") return;
    if ("key" in e) e.preventDefault();
    onRemove(option.value);
  };

  return (
    <div className="inline-flex h-full shrink-0 items-center gap-1 rounded-[8px] bg-[var(--c-accent-bg)] px-2 py-0 text-xs leading-none font-normal text-[var(--c-accent)]">
      <div className="flex max-w-[160px] flex-col">
        <span
          className={cn(
            "truncate",
            !option.meta && option.label.includes("@")
              ? "lowercase"
              : "capitalize"
          )}
        >
          {option.label}
        </span>
      </div>
      {!disabled && (
        <span
          role="button"
          tabIndex={0}
          onClick={handleRemove}
          onKeyDown={handleRemove}
          className="cursor-pointer rounded-[6px] p-0.5 transition-colors hover:bg-[var(--c-accent)]/10"
          aria-label={`${option.label} entfernen`}
        >
          <X size={10} />
        </span>
      )}
    </div>
  );
}
